import React, { useState, useEffect } from "react";
import ChatBox from "./ChatBox";
import Navbar from "../header/Navbar";
import SyncPhone from "./SyncPhone";
import Dashboard from "./Dashboard";
import axios from "axios";
import Cookies from "js-cookie";
import { useParams } from "react-router-dom";
import Subscribe from "../Subscribe";
import { useNavigate } from "react-router-dom";
import {
  track,
  trackOnce,
  identify,
  incrementUserProperty,
  resetAnalytics,
  EVENTS,
} from "../../utils/analytics";

function Coach() {
  const [userInput, setUserInput] = useState("");
  const [conversation, setConversation] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showSyncPhone, setShowSyncPhone] = useState(false);
  const [view, setView] = useState("Dashboard");
  const [user, setUser] = useState({});
  const { phone } = useParams();
  const navigate = useNavigate(); // Import useNavigate from 'react-router-dom'

  const [darkTheme, setDarkTheme] = useState(() => {
    const savedTheme = localStorage.getItem("darkTheme");
    return savedTheme !== null ? JSON.parse(savedTheme) : true;
  });
  // Try localStorage first, then fall back to cookies
  const mobile1440Id =
    localStorage.getItem("1440_mobile_id") || Cookies.get("1440_mobile_id");
  const sub1440Id =
    localStorage.getItem("1440_user_id") || Cookies.get("1440_user_id"); // Retrieve sub_1440_id
  const accessToken =
    localStorage.getItem("1440_access_token") ||
    Cookies.get("1440_access_token"); // Retrieve access token

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const viewParam = searchParams.get("view");
    if (viewParam) {
      setView(viewParam);
    }
  }, []); // Add this useEffect with an empty dependency array

  useEffect(() => {
    localStorage.setItem("darkTheme", JSON.stringify(darkTheme));
    if (sub1440Id && accessToken) {
      loadPreviousMessages();
    }
  }, [darkTheme, sub1440Id, accessToken]);

  useEffect(() => {
    if (!sub1440Id || !accessToken) {
      setShowSyncPhone(true); // Show phone sync if either is missing
    } else {
      fetchUser();
      // Authenticated app session: bind identity, then log the chat surface open.
      identify(sub1440Id);
      track(EVENTS.CHAT_OPENED, { view, has_phone_param: !!phone });
      // A valid token + user_id already proves the phone was verified during
      // login, so NEVER bounce an authenticated user back to the sync screen
      // just because the stored mobile_id string looks off. Keep them logged in.
      setShowSyncPhone(false);
    }
  }, [sub1440Id, accessToken, mobile1440Id]); // Added sub1440Id to dependencies

  const loadPreviousMessages = async () => {
    if (!sub1440Id) return;
    try {
      const response = await axios.post("/api/get_previous_session", {
        uid: sub1440Id,
      });

      if (response.status !== "false" && Array.isArray(response.data)) {
        setConversation(response.data);
      } else {
        setConversation([]); // Ensure we set an empty array if response is invalid
      }
    } catch (error) {
      console.log("Error loading previous messages:", error);
      setConversation([]); // Ensure we set an empty array on error
    }
  };

  const toggleView = (newView) => {
    track(EVENTS.CHAT_VIEW_CHANGED, { from_view: view, to_view: newView });
    // The Subscribe view IS the in-app paywall, so record the impression.
    if (newView === "Subscribe") {
      track(EVENTS.PAYWALL_VIEWED, { surface: "in_app_view", source: view });
    }
    setView(newView);
  };

  const sendMessage = async (message) => {
    // if (!sub1440Id.startsWith("+")) {
    //   setShowSyncPhone(true);
    //   return;
    // }
    // TODO : needed
    // if (!sub1440Id && !accessToken) {
    //   setShowSyncPhone(true);
    //   return;
    // }

    setConversation((prevConversation) => [
      ...prevConversation,
      { role: "user", content: message },
    ]);
    showTypingIndicator();

    // Message-level analytics. We send LENGTH and shape, never the body text.
    const messageIndex = conversation.length;
    const sentAt = Date.now();
    track(EVENTS.MESSAGE_SENT, {
      message_length: message ? message.length : 0,
      message_word_count: message ? message.trim().split(/\s+/).length : 0,
      message_index: messageIndex,
      is_authenticated: !!accessToken,
    });
    incrementUserProperty("web_messages_sent", 1);
    // Activation step 1, deduped for life so it stays a true funnel stage.
    trackOnce("first_message_sent", EVENTS.FIRST_MESSAGE_SENT, {
      message_length: message ? message.length : 0,
    });

    try {
      const response = await axios.post("/api/coach", {
        body: message,
        uid: sub1440Id,
      });

      const answer = response?.data?.answer || "";
      track(EVENTS.COACH_RESPONSE_RECEIVED, {
        response_time_ms: Date.now() - sentAt,
        response_length: answer.length,
        message_index: messageIndex,
      });
      // Activation step 2: FIRST VALUE MOMENT (user saw the coach reply).
      trackOnce("first_response_received", EVENTS.FIRST_RESPONSE_RECEIVED, {
        response_time_ms: Date.now() - sentAt,
        response_length: answer.length,
        is_first_value_moment: true,
      });

      setConversation((prevConversation) => [
        ...prevConversation,
        { role: "assistant", content: response.data.answer },
      ]);
    } catch (error) {
      track(EVENTS.COACH_RESPONSE_FAILED, {
        http_status: error?.response?.status || null,
        response_time_ms: Date.now() - sentAt,
        message_index: messageIndex,
      });
      setConversation((prevConversation) => [
        ...prevConversation,
        { role: "assistant", content: "An error occurred. Please try again." },
      ]);
    } finally {
      hideTypingIndicator();
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`/users/${sub1440Id}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setUser(response.data);
      // Attach durable traits so Mixpanel can segment by plan/protocol without
      // us having to re-send them on every event.
      identify(sub1440Id, {
        is_paying: !!response.data?.is_paying,
        current_protocol: response.data?.current_protocol || null,
        messages_count: response.data?.messages_count ?? null,
        timezone: response.data?.timezone || null,
        has_calendar_connected: !!response.data?.calendar,
        has_health_connected: !!response.data?.vital_user_id,
      });
    } catch (error) {
      console.error("Error fetching user:", error);

      // ONLY log the user out on a genuine auth rejection (401/403). Previously
      // ANY error here (network blip, transient 5xx, slow response) wiped the
      // tokens and reloaded -> user got bounced straight back to the OTP screen
      // even though they had just logged in successfully (the "login loop").
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // Session genuinely rejected: log it, then clear analytics identity so
        // the next person on this browser is not merged into this user.
        track(EVENTS.LOGGED_OUT, { reason: "auth_rejected", http_status: status });
        resetAnalytics();
        // Remove the relevant cookies
        Cookies.remove("1440_access_token");
        Cookies.remove("1440_user_id");
        Cookies.remove("1440_mobile_id");
        // Remove from localStorage as well to ensure a clean state after reload
        localStorage.removeItem("1440_access_token");
        localStorage.removeItem("1440_user_id");
        localStorage.removeItem("1440_mobile_id");
        // Reload the page to reflect the changes
        window.location.reload();
      }
      // For transient/network errors we keep the session and simply leave the
      // user's data unset; the next render / retry can recover without a logout.
    }
  };

  const showTypingIndicator = () => {
    setIsTyping(true);
  };

  const hideTypingIndicator = () => {
    setIsTyping(false);
  };

  const handlePhoneSync = (phoneNumber) => {
    Cookies.set("1440_mobile_id", phoneNumber, { expires: 7 });
    setShowSyncPhone(false);
    window.location.reload();
  };

  const closeSyncPhone = () => {
    setShowSyncPhone(false);
  };

  return (
    <div
      className={`App${
        darkTheme ? " dark" : ""
      } flex flex-col min-h-screen bg-white dark:bg-black text-black dark:text-white`}
    >
      <header className="bg-white dark:bg-black px-6">
        <Navbar
          userId={sub1440Id}
          onToggleView={(newView) => toggleView(newView)}
          view={view}
        />
      </header>
      {showSyncPhone ? (
        <SyncPhone
          onSync={handlePhoneSync}
          onCloseSync={closeSyncPhone}
          starterPhone={phone ? phone : false}
        />
      ) : view === "Dashboard" ? (
        <Dashboard user={user} />
      ) : view === "Chat" ? (
        <main className="flex-1 overflow-y-auto px-6 sm:px-10 md:px-12 bg-white dark:bg-black text-black dark:text-white">
          <ChatBox
            conversation={conversation}
            sendMessage={sendMessage}
            isTyping={isTyping}
            userInput={userInput}
            setUserInput={setUserInput}
          />
        </main>
      ) : view === "Subscribe" ? (
        <Subscribe setView={setView} />
      ) : (
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-black text-black dark:text-white">
          <p className="text-xl">This page is empty.</p>
        </div>
      )}
    </div>
  );
}

export default Coach;
